import {
  UserRole, TimelineActions, ProjectStages, ProjectTypes,
} from '../enums';
import MilestoneTimelines from './MilestoneTimelines';
import getForwardStageName from './getForwardStageName';

const timelinesNames = MilestoneTimelines.getInstance().getNames();
const timelinesIndexes = MilestoneTimelines.getInstance().getIndexes();
const checkIfMilestoneCompleted = (steps) => steps.every(({ isPassed, isObligatory }) => !isObligatory || isPassed);

const timelineDataConfig = [
  {
    index: timelinesIndexes.myBookProject,
    name: timelinesNames[timelinesIndexes.myBookProject],
    isLocked: () => false,
    steps: [
      {
        name: 'bookProjectSetUp',
        date: (timelineData) => timelineData?.find((i) => i.message === TimelineActions.PROJECT_CREATED)?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'editorConfirmed',
        date: (timelineData) => timelineData?.find((i) => i.message === TimelineActions.EDITOR_ADDED)?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'packageSelected',
        date: (timelineData) => timelineData?.find((i) => i.message === TimelineActions.PROJECT_CREATED)?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
    ],
    isActionRequired: () => false,
  },
  {
    index: timelinesIndexes.myWriterMatch,
    name: timelinesNames[timelinesIndexes.myWriterMatch],
    isLocked: () => false,
    steps: [
      {
        name: 'questionnaireSentToCustomer',
        date: (timelineData) => timelineData?.find((i) => [
          TimelineActions.QUESTIONNAIRE_SENT,
          getForwardStageName(ProjectStages.QUESTIONNAIRE_AND_WRITER_MATCH.name),
        ].includes(i.message))?.date,
        isObligatory: false,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'questionnaireCompleted',
        date: (timelineData) => timelineData?.find((i) => [
          TimelineActions.QUESTIONNAIRE_COMPLETED,
          getForwardStageName(ProjectStages.QUESTIONNAIRE_AND_WRITER_MATCH.name),
        ].includes(i.message))?.date,
        isObligatory: false,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'writerAdded',
        date: (timelineData) => timelineData?.find((i) => i.message === TimelineActions.WRITER_ADDED)?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
    ],
    isActionRequired: (steps) => {
      const isQuestionnaireCompleted = steps?.find((step) => step.name === 'questionnaireCompleted').isPassed;
      const milestoneCompleted = checkIfMilestoneCompleted(steps);
      return !isQuestionnaireCompleted && !milestoneCompleted;
    },
    goToStep: () => ({ path: '/questionnaire' }),
  },
  {
    index: timelinesIndexes.myInterviews,
    name: timelinesNames[timelinesIndexes.myInterviews],
    isLocked: (stages) => stages.findIndex((i) => i.name === ProjectStages.INTERVIEWS_SCHEDULING.name) < 0,
    steps: [
      {
        name: 'interviewSchedulingBegun',
        date: (timelineData) => timelineData?.find((i) => [
          TimelineActions.INTERVIEW_SCHEDULED,
          getForwardStageName(ProjectStages.INTERVIEWS_SCHEDULING.name),
        ].includes(i.message))?.date,
        isObligatory: false,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'interviewAudioFileUploaded',
        date: (timelineData) => timelineData?.find((i) => TimelineActions.INTERVIEW_AUDIO_UPLOADED === i.message)?.date,
        isObligatory: false,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'interviewTranscriptFileUploaded',
        date: (timelineData) => timelineData?.find(
          (i) => TimelineActions.INTERVIEW_TRANSCRIPT_UPLOADED === i.message,
        )?.date,
        isObligatory: false,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'interviewStageCompleted',
        date: (timelineData) => timelineData?.find((i) => [
          TimelineActions.COMPLETE_INTERVIEW_STAGE,
          getForwardStageName(ProjectStages.INTERVIEW_STAGE.name),
          getForwardStageName(ProjectStages.CREATING_OUTLINE.name),
        ].includes(i.message)
        || (i.message === TimelineActions.OUTLINE_SUBMITTED && i.role === UserRole.WRITER))?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
    ],
    isActionRequired: () => false,
    goToStep: () => ({ path: '/interviews' }),
  },
  {
    index: timelinesIndexes.myBookOutlineAndSample,
    name: timelinesNames[timelinesIndexes.myBookOutlineAndSample],
    isLocked: (stages) => stages.findIndex((i) => i.name === ProjectStages.OUTLINE_APPROVAL_BY_CUSTOMER.name) < 0,
    steps: [
      {
        name: 'outlineSubmittedByWriter',
        date: (timelineData) => timelineData?.find(
          (i) => (i.message === TimelineActions.OUTLINE_SUBMITTED && i.role === UserRole.WRITER)
            || (getForwardStageName(ProjectStages.CREATING_OUTLINE.name) === i.message),
        )?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'outlineApprovedByEditor',
        date: (timelineData) => timelineData?.find(
          (i) => (i.message === TimelineActions.OUTLINE_APPROVED && i.role === UserRole.EDITOR)
            || (getForwardStageName(ProjectStages.OUTLINE_APPROVAL_BY_EDITOR.name) === i.message),
        )?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'outlineApprovedByCustomer',
        date: (timelineData) => timelineData?.find(
          (i) => (i.message === TimelineActions.OUTLINE_APPROVED && i.role === UserRole.CUSTOMER)
            || (getForwardStageName(ProjectStages.OUTLINE_APPROVAL_BY_CUSTOMER.name) === i.message),
        )?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'sampleApprovedByWriter',
        date: (timelineData) => timelineData?.find(
          (i) => {
            const isMatchingMessage = i.message === TimelineActions.SAMPLE_SUBMITTED;

            return (isMatchingMessage && i.role === UserRole.WRITER)
              || (getForwardStageName(ProjectStages.CREATING_SAMPLE.name) === i.message);
          },
        )?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'sampleApprovedByEditor',
        date: (timelineData) => timelineData?.find(
          (i) => {
            const isMatchingMessage = i.message === TimelineActions.SAMPLE_APPROVED;

            return (isMatchingMessage && [UserRole.EDITOR, UserRole.ADMIN].includes(i.role))
              || (getForwardStageName(ProjectStages.SAMPLE_APPROVAL_BY_EDITOR.name) === i.message);
          },
        )?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'sampleApprovedByCustomer',
        date: (timelineData) => timelineData?.find(
          (i) => {
            const isMatchingMessage = i.message === TimelineActions.SAMPLE_APPROVED
              || i.message === TimelineActions.STYLE_SAMPLE_APPROVED;

            return (isMatchingMessage && i.role === UserRole.CUSTOMER)
              || (getForwardStageName(ProjectStages.SAMPLE_APPROVAL_BY_CUSTOMER.name) === i.message);
          },
        )?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
    ],
    isActionRequired: (steps) => {
      const isOutlineSubmittedByWriter = steps?.find((step) => step.name === 'outlineSubmittedByWriter').isPassed;
      const isOutlineApprovedByEditor = steps?.find((step) => step.name === 'outlineApprovedByEditor').isPassed;
      const isOutlineApprovedByCustomer = steps?.find((step) => step.name === 'outlineApprovedByCustomer').isPassed;
      const isSampleApprovedByCustomer = steps?.find((step) => step.name === 'sampleApprovedByCustomer').isPassed;
      const milestoneCompleted = checkIfMilestoneCompleted(steps);
      return isOutlineSubmittedByWriter
        && isOutlineApprovedByEditor
        && !isOutlineApprovedByCustomer
        && !isSampleApprovedByCustomer
        && !milestoneCompleted;
    },
    goToStep: (steps, isSwiftEnabled) => {
      const isOutlineApprovedByCustomer = steps?.find((step) => step.name === 'outlineApprovedByCustomer').isPassed;
      let pathToGo = '/outline';

      if (isOutlineApprovedByCustomer) {
        pathToGo = isSwiftEnabled ? '/style-samples' : '/book-sample';
      }

      return { path: pathToGo };
    },
  },
  {
    index: timelinesIndexes.uploadingMyPhotos,
    name: timelinesNames[timelinesIndexes.uploadingMyPhotos],
    isLocked: () => false,
    steps: [
      {
        name: 'firstPhotoUploaded',
        date: (timelineData) => timelineData?.find(
          (i) => i.message === TimelineActions.FIRST_PHOTO_UPLOADED
            && [UserRole.CUSTOMER, UserRole.EDITOR, UserRole.ADMIN].includes(i.role),
        )?.date,
        isObligatory: false,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'firstCaptionAdded',
        date: (timelineData) => timelineData?.find(
          (i) => i.message === TimelineActions.ADDED_FIRST_CAPTION
            && [UserRole.CUSTOMER, UserRole.EDITOR, UserRole.ADMIN].includes(i.role),
        )?.date,
        isObligatory: false,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'photoUploadingCompleted',
        date: (timelineData) => timelineData?.find(
          (i) => [TimelineActions.ALL_PHOTOS_UPLOADED, TimelineActions.PHOTOS_SUBMITTED].includes(i.message)
            && [UserRole.CUSTOMER, UserRole.EDITOR, UserRole.ADMIN].includes(i.role),
        )?.date,
        isObligatory: false,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'photosAllocatedToChapters',
        date: (timelineData) => timelineData?.find(
          (i) => [TimelineActions.ALL_PHOTOS_ALLOCATED, TimelineActions.PHOTOS_SUBMITTED].includes(i.message)
            && [UserRole.CUSTOMER, UserRole.EDITOR, UserRole.ADMIN].includes(i.role),
        )?.date,
        isObligatory: false,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
    ],
    isActionRequired: (steps, currentMilestoneIndex) => {
      const isPhotoUploadingCompleted = steps?.find((step) => step.name === 'photoUploadingCompleted').isPassed;
      const isPhotosAllocatedToChapters = steps?.find((step) => step.name === 'photosAllocatedToChapters').isPassed;
      const isMilestoneEarlierMyBookOutlineAndSample = timelinesIndexes.myBookOutlineAndSample > currentMilestoneIndex;
      return (!isPhotoUploadingCompleted && isMilestoneEarlierMyBookOutlineAndSample)
        || (!isPhotosAllocatedToChapters && !isMilestoneEarlierMyBookOutlineAndSample);
    },
    goToStep: () => ({ path: '/photos' }),
    videoHref: 'https://www.loom.com/share/cb9f07902d4d417cb2b99d93d3d4d8dc',
  },
  {
    index: timelinesIndexes.myDraftManuscript,
    name: timelinesNames[timelinesIndexes.myDraftManuscript],
    isLocked: (stages) => stages.findIndex((i) => i.name === ProjectStages.DRAFT_APPROVAL_BY_CUSTOMER.name) < 0,
    steps: [
      {
        name: 'draftSubmittedByWriter',
        date: (timelineData) => timelineData?.find(
          (i) => (i.message === TimelineActions.DRAFT_SUBMITTED && i.role === UserRole.WRITER)
            || (getForwardStageName(ProjectStages.DRAFT_APPROVAL_BY_WRITER.name) === i.message),
        )?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'developmentalEditorAdded',
        date: (timelineData) => timelineData?.find(
          (i) => i.message === TimelineActions.DEVELOPMENTAL_EDITOR_ADDED,
        )?.date,
        isObligatory: false,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'proofreadCompleted',
        date: (timelineData) => timelineData?.find(
          (i) => (i.message === TimelineActions.PROOFREAD_COMPLETED)
          || (getForwardStageName(ProjectStages.DRAFT_DEVELOPMENTAL_EDITING.name) === i.message),
        )?.date,
        isObligatory: false,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'draftSubmittedToCustomer',
        date: (timelineData) => timelineData?.find(
          (i) => (i.message === TimelineActions.DRAFT_SUBMITTED_TO_CUSTOMER)
          || (getForwardStageName(ProjectStages.DRAFT_DEVELOPMENTAL_EDITING.name) === i.message)
          || (getForwardStageName(ProjectStages.DRAFT_APPROVAL_BY_EDITOR.name) === i.message),
        )?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'draftApprovedByCustomer',
        date: (timelineData) => timelineData?.find(
          (i) => (i.message === TimelineActions.DRAFT_APPROVED && i.role === UserRole.CUSTOMER)
          || (getForwardStageName(ProjectStages.DRAFT_APPROVAL_BY_CUSTOMER.name) === i.message),
        )?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
    ],
    isActionRequired: (steps) => {
      const isDraftSubmittedByWriter = steps?.find((step) => step.name === 'draftSubmittedByWriter').isPassed;
      const isDraftSubmittedToCustomer = steps?.find((step) => step.name === 'draftSubmittedToCustomer').isPassed; // Approved by Editor
      const isDraftApprovedByCustomer = steps?.find((step) => step.name === 'draftApprovedByCustomer').isPassed;
      const milestoneCompleted = checkIfMilestoneCompleted(steps);
      return isDraftSubmittedByWriter
        && isDraftSubmittedToCustomer
        && !isDraftApprovedByCustomer
        && !milestoneCompleted;
    },
    goToStep: () => ({ path: '/draft/1' }),
  },
  {
    index: timelinesIndexes.myBookCover,
    name: timelinesNames[timelinesIndexes.myBookCover],
    isLocked: () => false,
    steps: [
      {
        name: 'coverApproved',
        date: (timelineData) => timelineData?.find(
          (i) => i.message === TimelineActions.COVER_APPROVED
            && [UserRole.CUSTOMER, UserRole.EDITOR].includes(i.role),
        )?.date,
        isObligatory: false,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
    ],
    isActionRequired: (steps) => {
      const isNotAllStepCompleted = steps.some((step) => !step.isPassed);
      return isNotAllStepCompleted;
    },
    goToStep: () => ({ path: '/cover-creator' }),
  },
  {
    index: timelinesIndexes.proofOfMyBook,
    name: timelinesNames[timelinesIndexes.proofOfMyBook],
    isLocked: (stages) => stages.findIndex((i) => i.name === ProjectStages.FINAL_PROOF_APPROVAL_BY_CUSTOMER.name) < 0,
    steps: [
      {
        name: 'proofCreatedByEditor',
        date: (timelineData) => timelineData?.find(
          (i) => i.message === TimelineActions.PDF_GENERATED && i.role === UserRole.EDITOR,
        )?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'proofApprovedByCustomer',
        date: (timelineData) => timelineData?.find(
          (i) => (i.message === TimelineActions.PROOF_APPROVED && i.role === UserRole.CUSTOMER)
            || (getForwardStageName(ProjectStages.FINAL_PROOF_APPROVAL_BY_CUSTOMER.name) === i.message),
        )?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
      {
        name: 'bookSentToPrinter',
        date: (timelineData) => timelineData?.find((i) => i.message === TimelineActions.BOOK_SENT_TO_PRINTER)?.date,
        isObligatory: true,
        showFor: [ProjectTypes.CLASSIC, ProjectTypes.PRO, ProjectTypes.SWIFT, ProjectTypes.OCCASION_BOOK],
      },
    ],
    isActionRequired: (steps) => {
      const isProofApprovedByCustomer = steps?.find((step) => step.name === 'proofApprovedByCustomer').isPassed;
      const milestoneCompleted = checkIfMilestoneCompleted(steps);
      return !isProofApprovedByCustomer && !milestoneCompleted;
    },
    goToStep: () => ({ path: '/final-proof' }),
  },
];

export default timelineDataConfig;
